import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Code, Copy, Check, Users, Share2, Download, Settings,
  FileCode, Terminal, Database, Play, Wifi, WifiOff
} from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Supported languages
const LANGUAGES = [
  { id: 'yaml', name: 'YAML' },
  { id: 'bash', name: 'Shell / Bash' },
  { id: 'python', name: 'Python' },
  { id: 'javascript', name: 'JavaScript' },
  { id: 'typescript', name: 'TypeScript' },
  { id: 'json', name: 'JSON' },
  { id: 'sql', name: 'SQL' },
  { id: 'dockerfile', name: 'Dockerfile' },
  { id: 'markup', name: 'HTML/XML' },
  { id: 'css', name: 'CSS' },
  { id: 'go', name: 'Go' },
  { id: 'java', name: 'Java' },
  { id: 'hcl', name: 'Terraform (HCL)' },
  { id: 'ini', name: 'INI / Ansible' },
  { id: 'markdown', name: 'Markdown' },
];

export default function LiveCodeSharePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [code, setCode] = useState('// Commencez à coder ici...\n// Les modifications sont synchronisées en temps réel\n');
  const [language, setLanguage] = useState('javascript');
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [copied, setCopied] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  
  const wsRef = useRef(null);
  const editorRef = useRef(null);
  const localChangeId = useRef(0);
  const lastSentCode = useRef('');

  // Connect to WebSocket
  useEffect(() => {
    if (!sessionId) return;

    const connectWs = () => {
      const ws = new WebSocket(`${WS_URL}/api/codeshare/live/${sessionId}`);
      
      ws.onopen = () => {
        setConnected(true);
        // Send join message
        const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Anonyme';
        ws.send(JSON.stringify({
          type: 'join',
          userId: user?.id || 'anonymous',
          userName: userName || 'Anonyme'
        }));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'init':
              // Initial state from server - always apply
              lastSentCode.current = data.code || '';
              setCode(data.code || '');
              setLanguage(data.language || 'javascript');
              setParticipants(data.participants || []);
              setSessionInfo(data.session);
              break;
              
            case 'code_update':
              // Code update from another user - only skip if it's our own echo
              if (data.code !== lastSentCode.current) {
                setCode(data.code);
                lastSentCode.current = data.code;
              }
              break;
              
            case 'language_change':
              setLanguage(data.language);
              break;
              
            case 'participants_update':
              setParticipants(data.participants || []);
              break;
              
            case 'user_joined':
              toast.info(`${data.userName} a rejoint la session`);
              break;
              
            case 'user_left':
              toast.info(`${data.userName} a quitté la session`);
              break;
          }
        } catch (e) {
          console.error('WebSocket message error:', e);
        }
      };
      
      ws.onclose = () => {
        setConnected(false);
        // Reconnect after 3 seconds
        setTimeout(connectWs, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
      };
      
      wsRef.current = ws;
    };

    connectWs();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [sessionId, user]);

  // Send code update
  const handleCodeChange = useCallback((newCode) => {
    setCode(newCode);
    lastSentCode.current = newCode;
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'code_update',
        code: newCode
      }));
    }
  }, []);

  // Send language change
  const handleLanguageChange = useCallback((newLang) => {
    setLanguage(newLang);
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'language_change',
        language: newLang
      }));
    }
  }, []);

  // Copy share link
  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/live/${sessionId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Copy code
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copié !');
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Download code
  const downloadCode = () => {
    const extensions = {
      javascript: 'js',
      typescript: 'ts',
      python: 'py',
      yaml: 'yml',
      bash: 'sh',
      json: 'json',
      sql: 'sql',
      dockerfile: 'dockerfile',
      markup: 'html',
      css: 'css',
      go: 'go',
      java: 'java',
      hcl: 'tf',
      ini: 'ini',
      markdown: 'md'
    };
    
    const ext = extensions[language] || 'txt';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `codeshare-${sessionId}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get line count
  const lineCount = code.split('\n').length;

  return (
    <div className="min-h-screen bg-[#1e1e1e] flex flex-col">
      {/* Header */}
      <header className="bg-[#2d2d2d] border-b border-[#404040] px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-white">
              <Code className="h-6 w-6 text-blue-400" />
              <span className="text-lg font-semibold">CodeShare Live</span>
            </div>
            
            {/* Connection Status */}
            <Badge 
              variant={connected ? "default" : "destructive"}
              className="flex items-center gap-1"
            >
              {connected ? (
                <>
                  <Wifi className="h-3 w-3" />
                  Connecté
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  Déconnecté
                </>
              )}
            </Badge>
            
            {/* Participants */}
            <div className="flex items-center gap-2 text-gray-400">
              <Users className="h-4 w-4" />
              <span className="text-sm">{participants.length} participant{participants.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-[150px] bg-[#3c3c3c] border-[#505050] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2d2d2d] border-[#505050]">
                {LANGUAGES.map(lang => (
                  <SelectItem 
                    key={lang.id} 
                    value={lang.id}
                    className="text-gray-200 focus:bg-[#3c3c3c] focus:text-white"
                  >
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Action Buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={copyShareLink}
              className="bg-blue-600 hover:bg-blue-700 text-white border-blue-700"
            >
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
              Partager
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={copyCode}
              className="bg-[#3c3c3c] hover:bg-[#4c4c4c] text-white border-[#505050]"
            >
              <Copy className="h-4 w-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCode}
              className="bg-[#3c3c3c] hover:bg-[#4c4c4c] text-white border-[#505050]"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Editor */}
      <div className="flex-1 flex">
        {/* Line Numbers */}
        <div className="bg-[#1e1e1e] text-gray-500 text-right pr-4 pt-4 select-none font-mono text-sm border-r border-[#404040]" style={{ minWidth: '50px' }}>
          {Array.from({ length: Math.max(lineCount, 30) }, (_, i) => (
            <div key={i + 1} className="leading-6">
              {i + 1}
            </div>
          ))}
        </div>
        
        {/* Code Editor */}
        <div className="flex-1 relative">
          <textarea
            ref={editorRef}
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            className="absolute inset-0 w-full h-full bg-transparent text-gray-100 font-mono text-sm p-4 resize-none focus:outline-none leading-6 z-10"
            placeholder="// Commencez à coder ici..."
            spellCheck="false"
            style={{ 
              caretColor: 'white',
              tabSize: 2,
              fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace"
            }}
          />
          
          {/* Syntax Highlighting Overlay (optional, for visual feedback) */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-0">
            <Highlight theme={themes.vsDark} code={code} language={language}>
              {({ tokens, getLineProps, getTokenProps }) => (
                <pre className="p-4 font-mono text-sm leading-6">
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          </div>
        </div>
        
        {/* Sidebar - Participants */}
        <div className="w-48 bg-[#252526] border-l border-[#404040] p-3">
          <h3 className="text-gray-400 text-xs uppercase font-semibold mb-3 flex items-center gap-2">
            <Users className="h-3 w-3" />
            Participants
          </h3>
          <div className="space-y-2">
            {participants.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun participant</p>
            ) : (
              participants.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <div 
                    className="w-2 h-2 rounded-full bg-green-500"
                    title="En ligne"
                  />
                  <span className="truncate">{p.name || 'Anonyme'}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-[#007acc] text-white text-xs px-4 py-1 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span>{LANGUAGES.find(l => l.id === language)?.name || language}</span>
          <span>Lignes: {lineCount}</span>
          <span>Caractères: {code.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>Session: {sessionId}</span>
        </div>
      </footer>
    </div>
  );
}
